import * as React from "react";
import { useState, useEffect, useRef } from "react";
import QuestTabs from "@/components/molecules/quests/quest-tabs";
import QuestList from "@/components/molecules/quests/quest-list";
import { QuestType, Quest } from "@/types/quests";
import { questsBackend } from "@/services/questsBackend";
import { useTonConnect } from "@/hooks/useTonConnect";
import { Address } from "@ton/core";
import { TonConnectButton } from "@tonconnect/ui-react";
import MotionModal from "@/components/atomics/motion-modal";
import ConnectWalletModal from "@/components/atomics/connect-wallet-modal";
import { enqueueSnackbar, closeSnackbar } from "notistack";

interface QuestsProps {
  selectedTab: QuestType;
  onTabChange: (tab: QuestType) => void;
}

export default function Quests({ selectedTab, onTabChange }: QuestsProps) {
  const [quests, setQuests] = useState<Quest[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { wallet, connected } = useTonConnect();
  const [showWalletConnectionModal, setShowWalletConnectionModal] = useState(false);
  const tonButtonRef = useRef<HTMLDivElement>(null);
  const hasShownSnackbar = useRef(false);
  
  console.log("Wallet to call quests:", wallet);
  
  useEffect(() => {
    if (connected) {
      setShowWalletConnectionModal(false);
      hasShownSnackbar.current = false;
      fetchQuests();
    } else {
      // Show snackbar when wallet is not connected
      if (!hasShownSnackbar.current) {
        enqueueSnackbar("Please connect your TON wallet to complete quests", {
          SnackbarProps: {
            onClick: () => {
              closeSnackbar();
            },
          },
          variant: "warning",
          autoHideDuration: 3000,
        });
        hasShownSnackbar.current = true;
      }
      // Still fetch quests to show the list
      fetchQuests();
    }
  }, [selectedTab, wallet, connected]);

  const fetchQuests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log("Wallet to call quests 2:", wallet);
      const response = await questsBackend.getQuestsList(wallet ? Address.parse(wallet).toString() : undefined);
      console.log("Quests list:", response);
      if (response.success) {
        setQuests(response.quests);
      } else {
        setError("Failed to fetch quests");
      }
    } catch (err) {
      setError("An error occurred while fetching quests");
      console.error("Error fetching quests:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectWallet = () => {
    tonButtonRef.current?.querySelector("button")?.click();
  };

  const handleOpenModal = () => {
    setShowWalletConnectionModal(true);
  };

  return (
    <div>
      <MotionModal className="z-[15]" isOpen={showWalletConnectionModal}>
        <ConnectWalletModal
          onClose={() => setShowWalletConnectionModal(false)}
          onClick={() => setShowWalletConnectionModal(false)}
          onConnect={handleConnectWallet}
        />
      </MotionModal>
      
      <div ref={tonButtonRef} className="absolute opacity-0 pointer-events-none">
        <TonConnectButton />
      </div>

      <div className="relative w-full aspect-[3/1]">
        <img src="/assets/quests/background.gif" alt="quests-bg" />
        <div className="absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center">
          <h1 className="text-[44px]">QUESTS</h1>
        </div>
      </div>

      <QuestTabs selectedTab={selectedTab} onTabChange={onTabChange} />

      {error ? (
        <div className="text-primary-red text-center mt-5">Error: {error}</div>
      ) : (
        <QuestList 
          quests={quests} 
          isLoading={isLoading}
          onRequestWalletConnection={handleOpenModal}
        />
      )}
    </div>
  );
}
